import { supabase } from '../config/supabase';

// Get API base URL based on environment
const getApiBaseUrl = (): string => {
  // Explicit override wins (build-time env var)
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  // The CRA dev server (:3000) has to talk to Express cross-origin; everywhere
  // else the frontend is served by Express itself, so a relative path keeps
  // this working on any domain.
  const isCraDevServer = window.location.hostname === 'localhost' && window.location.port === '3000';
  return isCraDevServer
    ? 'http://localhost:8000/api'
    : '/api';
};

const API_BASE_URL = getApiBaseUrl();

// Global auth context reference - will be set by the app
let authContext: any = null;

// Function to set auth context (called from App.tsx)
export const setAuthContext = (context: any) => {
  authContext = context;
};

class ApiClient {
  private async getAuthHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0',
    };

    // Use AuthContext if available, otherwise fall back to direct Supabase call
    let accessToken: string | null = null;
    
    if (authContext?.session?.access_token) {
      accessToken = authContext.session.access_token;
    } else {
      // Fallback: try to get session directly from Supabase
      try {
        const { data: { session } } = await supabase.auth.getSession();
        accessToken = session?.access_token || null;
      } catch (error) {
        console.warn('Failed to get session from Supabase:', error);
      }
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    } else {
      console.warn('No authentication token available for API request');
    }

    return headers;
  }

  async get<T>(endpoint: string): Promise<T> {
    const headers = await this.getAuthHeaders();
    const url = `${API_BASE_URL}${endpoint}`;
    
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
      cache: 'no-cache',
    });

    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error:', errorText);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    return data;
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    const headers = await this.getAuthHeaders();
    const url = `${API_BASE_URL}${endpoint}`;
    
    console.log('Making POST request to:', url, data);
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: data ? JSON.stringify(data) : undefined,
      cache: 'no-cache',
    });


    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error:', errorText);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    return result;
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    const headers = await this.getAuthHeaders();
    const url = `${API_BASE_URL}${endpoint}`;
    
    console.log('Making PUT request to:', url, data);
    
    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: data ? JSON.stringify(data) : undefined,
      cache: 'no-cache',
    });


    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error:', errorText);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    return result;
  }

  async delete<T>(endpoint: string): Promise<T> {
    const headers = await this.getAuthHeaders();
    const url = `${API_BASE_URL}${endpoint}`;
    
    console.log('Making DELETE request to:', url);
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers,
      cache: 'no-cache',
    });


    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error:', errorText);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    return result;
  }

  async uploadFiles<T>(endpoint: string, files: File[], additionalData?: Record<string, any>): Promise<T> {
    const headers: HeadersInit = {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0',
    };
    
    // Use AuthContext if available, otherwise fall back to direct Supabase call
    let accessToken: string | null = null;
    
    if (authContext?.session?.access_token) {
      accessToken = authContext.session.access_token;
      headers['Authorization'] = `Bearer ${accessToken}`;
      console.log('Upload with token:', accessToken?.substring(0, 20) + '...');
    } else {
      console.log('Upload without token - no session found');
    }

    const url = `${API_BASE_URL}${endpoint}`;
    console.log('Making upload request to:', url);

    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    // Add additional data to formData
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
      cache: 'no-cache',
    });

    console.log('Upload response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Upload Error:', errorText);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    console.log('Upload Response:', result);
    return result;
  }
}

export const apiClient = new ApiClient(); 